/**
 * CockpitStore — Zustand store for the AI chatbot panel.
 *
 * Manages: panel open/close, active session, message list,
 * streaming state (response text + reasoning text), and session list.
 */

import { create } from 'zustand';
import { getValidToken } from '../auth/tokenManager';
import {
  createSession,
  listSessions,
  getMessages,
  deleteSession,
  renameSession,
  streamChat,
  type CockpitSession,
  type CockpitMessage,
} from '../services/CockpitService';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface ToolCall {
  id: string;
  name: string;
  status: 'running' | 'success' | 'error';
  result?: string;
}

export interface SessionContext {
  scope: 'machine' | 'line' | 'general';
  contextString: string;
  label: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Reasoning / thinking text (assistant only) */
  reasoning?: string;
  /** MCP tool calls made during this response */
  toolCalls?: ToolCall[];
  createdAt: string;
}

interface CockpitState {
  // ── Panel UI ─────────────────────────────────────────────────────────────
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  // ── Session ──────────────────────────────────────────────────────────────
  activeSessionId: string | null;
  /** Context locked when the conversation was started */
  sessionContext: SessionContext | null;
  sessions: CockpitSession[];
  sessionsLoading: boolean;

  loadSessions: (token: string) => Promise<void>;
  startNewSession: (token: string, context?: SessionContext) => Promise<string>;
  openSession: (token: string, sessionId: string) => Promise<void>;
  removeSession: (token: string, sessionId: string) => Promise<void>;
  renameSessionTitle: (token: string, sessionId: string, title: string) => Promise<void>;

  // ── Messages ─────────────────────────────────────────────────────────────
  messages: ChatMessage[];
  messagesLoading: boolean;

  // ── Streaming ────────────────────────────────────────────────────────────
  isStreaming: boolean;
  streamingText: string;
  streamingReasoning: string;
  streamingToolCalls: ToolCall[];
  abortController: AbortController | null;

  sendMessage: (token: string, text: string, context?: SessionContext) => Promise<void>;
  cancelStream: () => void;

  // ── Cleanup ──────────────────────────────────────────────────────────────
  reset: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

let msgCounter = 0;
function tempId(): string {
  return `local-${Date.now()}-${++msgCounter}`;
}

/**
 * The backend may store assistant message content as JSON:
 *   {"response": "...", "toolsUsed": [...]}
 * Extract only the response text for display.
 */
function extractContent(raw: string): string {
  if (!raw || !raw.startsWith('{')) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed.response === 'string') return parsed.response;
  } catch { /* not JSON, use as-is */ }
  return raw;
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useCockpitStore = create<CockpitState>()((set, get) => ({
  // ── Panel UI ─────────────────────────────────────────────────────────────
  isOpen: false,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  // ── Session ──────────────────────────────────────────────────────────────
  activeSessionId: null,
  sessionContext: null,
  sessions: [],
  sessionsLoading: false,

  loadSessions: async (_token) => {
    set({ sessionsLoading: true });
    try {
      const freshToken = await getValidToken();
      const sessions = await listSessions(freshToken);
      set({ sessions, sessionsLoading: false });
    } catch (err) {
      console.error('CockpitStore: failed to load sessions', err);
      set({ sessionsLoading: false });
    }
  },

  startNewSession: async (_token, context) => {
    try {
      const freshToken = await getValidToken();
      const session = await createSession(freshToken);
      set((s) => ({
        activeSessionId: session.id,
        sessionContext: context ?? s.sessionContext ?? null,
        sessions: [session, ...s.sessions],
        messages: [],
        streamingText: '',
        streamingReasoning: '',
      }));
      return session.id;
    } catch (err) {
      console.error('CockpitStore: failed to create session', err);
      // Show error as a chat message so the user sees it
      const errorMsg: ChatMessage = {
        id: tempId(),
        role: 'assistant',
        content: `⚠️ Failed to create conversation. Please try again.`,
        createdAt: new Date().toISOString(),
      };
      set((s) => ({ messages: [...s.messages, errorMsg] }));
      throw err;
    }
  },

  openSession: async (_token, sessionId) => {
    set({ activeSessionId: sessionId, sessionContext: null, messages: [], messagesLoading: true });
    try {
      const freshToken = await getValidToken();
      const msgs = await getMessages(freshToken, sessionId);
      const chatMessages: ChatMessage[] = msgs.map((m: CockpitMessage) => ({
        id: m.id,
        role: m.role,
        content: m.role === 'assistant' ? extractContent(m.content) : m.content,
        createdAt: m.created_at,
      }));
      set({ messages: chatMessages, messagesLoading: false });
    } catch (err) {
      console.error('CockpitStore: failed to load messages', err);
      set({ messagesLoading: false });
    }
  },

  removeSession: async (_token, sessionId) => {
    const freshToken = await getValidToken();
    await deleteSession(freshToken, sessionId);
    set((s) => {
      const sessions = s.sessions.filter((se) => se.id !== sessionId);
      const isActive = s.activeSessionId === sessionId;
      return {
        sessions,
        ...(isActive ? { activeSessionId: null, messages: [] } : {}),
      };
    });
  },

  renameSessionTitle: async (_token, sessionId, title) => {
    const freshToken = await getValidToken();
    await renameSession(freshToken, sessionId, title);
    set((s) => ({
      sessions: s.sessions.map((se) =>
        se.id === sessionId ? { ...se, title } : se,
      ),
    }));
  },

  // ── Messages ─────────────────────────────────────────────────────────────
  messages: [],
  messagesLoading: false,

  // ── Streaming ────────────────────────────────────────────────────────────
  isStreaming: false,
  streamingText: '',
  streamingReasoning: '',
  streamingToolCalls: [],
  abortController: null,

  sendMessage: async (_token, text, context) => {
    // Add user message to the list immediately
    const userMsg: ChatMessage = {
      id: tempId(),
      role: 'user',
      content: text,
      createdAt: new Date().toISOString(),
    };
    set((s) => ({ messages: [...s.messages, userMsg] }));

    let sessionId = get().activeSessionId;
    let fullText = '';
    let fullReasoning = '';
    let toolCalls: ToolCall[] = [];
    let toolCounter = 0;

    try {
      // Get a fresh token for the entire operation
      const freshToken = await getValidToken();

      // Lock context on first message if not already set
      const sessionCtx = get().sessionContext ?? context ?? null;
      if (!get().sessionContext && sessionCtx) {
        set({ sessionContext: sessionCtx });
      }

      // Ensure we have a session
      if (!sessionId) {
        sessionId = await get().startNewSession(freshToken, sessionCtx ?? undefined);
      }

      // Prepare the message with context
      const contextString = sessionCtx && sessionCtx.scope !== 'general' ? sessionCtx.contextString : undefined;
      const messageWithContext =
        contextString ? `${contextString}\n\n${text}` : text;

      // Start streaming
      const abortController = new AbortController();
      set({
        isStreaming: true,
        streamingText: '',
        streamingReasoning: '',
        streamingToolCalls: [],
        abortController,
      });

      for await (const event of streamChat(
        freshToken,
        sessionId,
        messageWithContext,
        abortController.signal,
      )) {
        switch (event.type) {
          case 'text_delta':
            fullText += event.text ?? '';
            set({ streamingText: fullText });
            break;
          case 'reasoning_delta':
            fullReasoning += event.text ?? '';
            set({ streamingReasoning: fullReasoning });
            break;
          case 'tool_call': {
            const tc: ToolCall = {
              id: `tool-${++toolCounter}`,
              name: event.text ?? 'unknown',
              status: event.block_type === 'running' ? 'running' : 'running',
            };
            toolCalls = [...toolCalls, tc];
            set({ streamingToolCalls: [...toolCalls] });
            break;
          }
          case 'tool_result': {
            const lastIdx = toolCalls.length - 1;
            if (lastIdx >= 0) {
              toolCalls[lastIdx] = {
                ...toolCalls[lastIdx],
                status: event.block_type === 'error' ? 'error' : 'success',
                result: event.text,
              };
              toolCalls = [...toolCalls];
              set({ streamingToolCalls: [...toolCalls] });
            }
            break;
          }
          case 'message_stop': {
            // Backend may include the full response in message_stop
            const finalText = event.response ?? event.text;
            if (finalText && !fullText) {
              fullText = finalText;
              set({ streamingText: fullText });
            }
            break;
          }
          case 'error':
            throw new Error(event.error ?? 'Stream error');
        }
      }

      // Add completed assistant message
      const assistantMsg: ChatMessage = {
        id: tempId(),
        role: 'assistant',
        content: fullText,
        reasoning: fullReasoning || undefined,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        createdAt: new Date().toISOString(),
      };
      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isStreaming: false,
        streamingText: '',
        streamingReasoning: '',
        streamingToolCalls: [],
        abortController: null,
      }));

      // Refresh session list to get updated titles/message counts
      get().loadSessions(freshToken);
    } catch (err) {
      if ((err as Error).name === 'AbortError') {
        // User cancelled — save what we have so far
        if (fullText) {
          const partialMsg: ChatMessage = {
            id: tempId(),
            role: 'assistant',
            content: fullText,
            reasoning: fullReasoning || undefined,
            createdAt: new Date().toISOString(),
          };
          set((s) => ({
            messages: [...s.messages, partialMsg],
          }));
        }
      } else {
        console.error('CockpitStore: stream error', err);
        // Add an error indicator message
        const errorMsg: ChatMessage = {
          id: tempId(),
          role: 'assistant',
          content: `⚠️ ${(err as Error).message || 'An error occurred. Please try again.'}`,
          createdAt: new Date().toISOString(),
        };
        set((s) => ({
          messages: [...s.messages, errorMsg],
        }));
      }
      set({ isStreaming: false, streamingText: '', streamingReasoning: '', streamingToolCalls: [], abortController: null });
    }
  },

  cancelStream: () => {
    const { abortController } = get();
    abortController?.abort();
  },

  // ── Cleanup ──────────────────────────────────────────────────────────────
  reset: () => {
    const { abortController } = get();
    abortController?.abort();
    set({
      isOpen: false,
      activeSessionId: null,
      sessionContext: null,
      sessions: [],
      messages: [],
      isStreaming: false,
      streamingText: '',
      streamingReasoning: '',
      abortController: null,
    });
  },
}));
